import React, { useEffect, useState, useRef } from "react";
import { useUpdateEffect } from "react-use";
import { Parser } from "html-to-react";
import { Buffer } from "buffer";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";

// Layout/UI
import { Box, Card, Icon, Stack, MetreaTooltip } from "@metrea-ui/v2";
import ItemOptions from "./ItemOptions";
import styles from "./ScenarioItems.module.css";
import itemStyles from "./ItemOptions.module.css";
import { AddSquareRegular, SubtractSquareRegular } from "@fluentui/react-icons";

// Utilities
import { mColor, getContrastYIQ } from "src/utils/themeUtils";
import {
	applyAccelerate,
	applyAddCut,
	applyDelay,
	applyNewDevelop,
	applyRetireMoreLess,
	applyTerminate,
} from "src/utils/scenarioUtils";
import { debugLog, showTesting } from "src/utils/debugUtils";
import {
	filterObjectsByProperty,
	getValuesByPropertyPath,
	findObjectByProperty,
	deepClone,
} from "src/utils/dataUtils";
import { applyFormatToNumber, highlight } from "src/utils/formatUtils";
import { isDeveloper } from "src/utils/sessionUtils";

const imgPlaceholder = "/No_Image_Available.jpg";

/**
 * ScenarioItem
 *
 * Represents a single scenario item row within a group, handling rendering, expansion,
 * and selection/configuration logic for that item across all periods.
 * - Handles internal UI state for expansion, selection options, and current values.
 * - Computes derived state (budget change, icon status, etc.) as user interacts.
 * - Emits all changes upward to parent via props.onChange for correct scenario recomputation.
 *
 * Props:
 * @param {object} props
 * @param {object} props.item - Scenario item object (with configs, baseline, inventory, etc.)
 * @param {object} props.scenario - The parent scenario (used for configs, periods, etc.)
 * @param {string} props.mode - "edit" or "view" mode for the UI.
 * @param {boolean} props.expanded - Whether the item starts expanded or collapsed.
 * @param {string} props.keywords - Highlight keywords for name/label search.
 * @param {function} props.onChange - Handler for selection/config changes (bubbles up to parent).
 * @param {function} props.onShowItem - Handler for clicking to show item details (modal).
 * @param {function} props.onError - Handler for error events.
 *
 * @component
 */
const ScenarioItem = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	useEffect(() => {
		debugLog(2, "[ScenarioItem][FLOW] mounted", {});
	}, []);

	const htmlParser = new Parser();

	// Custom hook
	const { lookups } = useLookupsData();

	const [thisItem, setThisItem] = useState(props.item);
	const [isExpanded, setIsExpanded] = useState(true);
	const [selectionOptions, setSelectionOptions] = useState([]);
	const [currValues, setCurrValues] = useState([]);
	const [iconStatus, setIconStatus] = useState([]);

	// --- Effect: Initializes selectionOptions and current values on first load based on thisItem
	useEffect(() => {
		setSelectionOptions(getSelectionOptions(thisItem));

		debugLog(3, "[ScenarioItem][STATE] init selectionOptions", {
			thisItem: thisItem,
			selectionOptions: selectionOptions,
		});

		if (thisItem.option_type_id === 304) {
			setCurrValues(
				getValuesByPropertyPath(
					filterObjectsByProperty(thisItem.scenario_item_configs, "comp_type_id", 406),
					"comp_value1",
				).map(Number),
			);
		} else if (thisItem.option_type_id === 302 || thisItem.option_type_id === 303) {
			setCurrValues(
				getValuesByPropertyPath(
					filterObjectsByProperty(thisItem.scenario_item_configs, "comp_type_id", 404),
					"comp_value1",
				).map(Number),
			);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// --- Effect: Syncs thisItem local state whenever props.item changes
	useUpdateEffect(() => {
		if (!props.item) return;
		setThisItem(deepClone(props.item));
		debugLog(3, "[ScenarioItem][STATE] props.item changed", { propsItem: props.item });
	}, [props.item]);

	// --- Effect: Updates iconStatus whenever selectionOptions changes
	useUpdateEffect(() => {
		let array401 = [];
		let array402 = [];
		let array403 = [];
		let array408 = [];
		selectionOptions.map((s, idx) => {
			s.map((x) => {
				if (x.comp_type_id === 401) array401.push(x.isDelay ? 1 : 0);
				if (x.comp_type_id === 402) array402.push(x.isAccelerate ? 1 : 0);
				if (x.comp_type_id === 403) array403.push(x.isTerminate ? 1 : 0);
				if (x.comp_type_id === 408) array408.push(x.isNewDevelop ? 1 : 0);
				return false;
			});
			return false;
		});

		setIconStatus([
			{ comp_type_id: 401, values: array401 },
			{ comp_type_id: 402, values: array402 },
			{ comp_type_id: 403, values: array403 },
			{ comp_type_id: 408, values: array408 },
		]);

		debugLog(4, "[ScenarioItem][PROCESS] selectionOptions -> iconStatus", {
			selectionOptions: selectionOptions,
			iconStatus: [
				{ comp_type_id: 401, values: array401 },
				{ comp_type_id: 402, values: array402 },
				{ comp_type_id: 403, values: array403 },
				{ comp_type_id: 408, values: array408 },
			],
		});
	}, [selectionOptions]);

	// --- Effect: Updates expansion state if props.expanded changes
	useEffect(() => {
		setIsExpanded(props.expanded);
		debugLog(3, "[ScenarioItem][STATE] props.expanded changed", { expanded: props.expanded });
	}, [props.expanded]);

	// --- Effect: Updates selectionOptions max value (for retire logic) whenever thisItem.total_change changes
	useUpdateEffect(() => {
		selectionOptions.map((x, index) => {
			if (x[0].comp_type_id === 406) {
				x[0].max =
					parseInt(thisItem.total_existing_inventory) -
					(parseInt(thisItem.total_planned) + parseInt(thisItem.total_change)) +
					parseInt(currValues[index]);
			}

			debugLog(9, "[ScenarioItem][PROCESS] total_change firehose", {
				index: index,
				max: x[0].max,
				total_existing_inventory: thisItem.total_existing_inventory,
				total_planned: thisItem.total_planned,
				total_change: thisItem.total_change,
				currValues: currValues,
				"currValues[index]": currValues[index],
			});

			return false;
		});
		setSelectionOptions([...selectionOptions]);

		debugLog(4, "[ScenarioItem][PROCESS] total_change recompute", {
			thisItem: thisItem,
			currValues: currValues,
			selectionOptions: selectionOptions,
		});
	}, [thisItem.total_change]);

	// --- Effect: Revert data
	useUpdateEffect(() => {
		const cloneItem = deepClone(props.item);
		setThisItem(cloneItem);
		setSelectionOptions(getSelectionOptions(cloneItem));
	}, [props.revertKey]);

	// --- Utilities ---
	const optionsIds = getValuesByPropertyPath(
		filterObjectsByProperty(lookups.SN_LOOKUPS_SEL_OPTIONS, "group_id", thisItem.option_type_id),
		"lookup_id",
	);

	/**
	 * Handles selection/option change from child ItemOptions (per period/option).
	 * Updates local state and emits the correct change data upward via props.onChange.
	 * @param {object} option - The changed option object (usually with {id, value}).
	 */
	const optionChangeHandler = (option) => {
		debugLog(3, "[ScenarioItem][ACTION] optionChangeHandler", { option: option });
		const { id, value } = option;
		const target = JSON.parse(id);
		const iconIds = [401, 402, 403, 408];

		// let updatedThisItem = { ...thisItem };
		// With actual deep clone
		let updatedThisItem = deepClone(thisItem);

		let updatedCurrValues = [...currValues];
		let updatedIconStatus = iconStatus.map((obj) => ({ ...obj }));

		if (iconIds.indexOf(target.comp_type_id) === -1) {
			updatedCurrValues[target.index] = value;

			// Retire more/less
			updatedThisItem.total_change = updatedCurrValues.reduce((a, b) => parseInt(a) + parseInt(b), 0);

			debugLog(9, "[ScenarioItem][PROCESS] optionChangeHandler.retireMoreLess", {
				selectionOptions: selectionOptions,
				target: target,
				currValues: currValues,
			});
		} else if (iconIds.indexOf(target.comp_type_id) !== -1) {
			updatedIconStatus.forEach((x) => {
				if (x.comp_type_id === target.comp_type_id) {
					x.values[target.index] = value;
				}
			});

			if (target.comp_type_id === 403) {
				filterObjectsByProperty(updatedThisItem.scenario_item_configs, "comp_type_id", 404).forEach(
					(x) => {
						if (x.sort_id > target.index) x.comp_value1 = "0";
					},
				);
			}
		}

		// Compute all derived data
		// recaculate the newSelectionOptions using updated data
		const newSelectionOptions = getSelectionOptions(updatedThisItem);
		const finalThisItem = getBudgetChange(newSelectionOptions, option, updatedThisItem);

		// uppdate *only* local state needed for UI
		setCurrValues(updatedCurrValues);
		setThisItem(finalThisItem);
		setIconStatus(updatedIconStatus);

		debugLog(3, "[ScenarioItem][ACTION] optionChangeHandler", {
			option: option,
			thisItem: thisItem,
			finalThisItem: finalThisItem,
		});

		// data passed up from itemOptions
		props.onChange({ option: option, item: finalThisItem });
	};

	/**
	 * Calculates and returns a new item object with derived budget/quantity changes
	 * based on the current selection.
	 * @param {array} selOpts - Selection options.
	 * @param {object} option - Changed option {id, value}.
	 * @param {object} currItem - The current item data.
	 * @returns {object} Updated item with budget/quantity change data.
	 */
	const getBudgetChange = (selOpts, option, currItem) => {
		const { id, value } = option;
		const newChange = JSON.parse(id);
		newChange.value = value;

		let tempBudgetChangeArray = [];
		let tempQuantityChangeArray = [];
		let budgetChangeTotal = 0;
		let quantityChangeTotal = 0;
		selOpts.map((x, idx1) => {
			let tempArray1 = [];
			let tempArray2 = [];
			x.map((y, idx2) => {
				tempArray1.push(y.budgetChange);
				tempArray2.push(y.quantityChange);
				budgetChangeTotal += y.budgetChange;
				quantityChangeTotal += y.quantityChange;
				return false;
			});
			tempBudgetChangeArray.push(tempArray1);
			tempQuantityChangeArray.push(tempArray2);
			return false;
		});

		// let updateItem = { ...thisItem };
		// With actual deep clone
		let updateItem = deepClone(currItem);

		switch (parseInt(newChange.comp_type_id)) {
			case 401:
				// Delay
				debugLog(9, "[ScenarioItem][PROCESS] applyDelay", { newChange });
				updateItem = applyDelay(updateItem, newChange);
				break;

			case 402:
				// Accelerate
				debugLog(9, "[ScenarioItem][PROCESS] applyAccelerate", { newChange });
				updateItem = applyAccelerate(updateItem, newChange);
				break;

			case 403:
				// Terminate
				debugLog(9, "[ScenarioItem][PROCESS] applyTerminate", { newChange });
				updateItem = applyTerminate(updateItem, newChange);
				break;

			case 404:
				// Add/Cut
				debugLog(9, "[ScenarioItem][PROCESS] applyAddCut", { newChange });
				updateItem = applyAddCut(updateItem, newChange);
				break;

			case 406:
				// Retire more/less
				debugLog(9, "[ScenarioItem][PROCESS] applyRetireMoreLess", { newChange });
				updateItem = applyRetireMoreLess(updateItem, newChange);
				break;

			case 408:
				// New Develop
				debugLog(9, "[ScenarioItem][PROCESS] applyNewDevelop", { newChange });
				updateItem = applyNewDevelop(updateItem, newChange);
				break;

			default:
				break;
		}

		debugLog(4, "[ScenarioItem][PROCESS] getBudgetChange", {
			thisItem: thisItem,
			updateItem: updateItem,
			selOpts: selOpts,
			newChange: newChange,
		});

		return {
			...updateItem,
			budgetChange: tempBudgetChangeArray,
			quantityChange: tempQuantityChangeArray,
			budgetChangeTotal: budgetChangeTotal,
			quantityChangeTotal: quantityChangeTotal,
		};
	};

	// --- Handlers ---

	// Handles expansion/collapse toggle for this scenario item
	const expandedSwitchHandler = () => {
		setIsExpanded(!isExpanded);
		debugLog(3, "[ScenarioItem][ACTION] expandedSwitchHandler", { isExpanded: !isExpanded });
	};

	// Handler for showing item details in a modal when label/image is clicked
	const showItemHandler = (event) => {
		props.onShowItem(event.currentTarget.id);
		debugLog(3, "[ScenarioItem][ACTION] showItemHandler", { currentTarget: event.currentTarget });
	};

	/**
	 * Generates selection options for all periods based on item, baseline configs, and lookups.
	 * Ensures all possible scenario_item_configs are available for the item.
	 * @param {object} item - The scenario item.
	 * @returns {array} selOpts - Array of selection option arrays, one per period.
	 */
	const getSelectionOptions = (item) => {
		// const getSelectionOptions = (item, currValues = []) => {
		let selOpts = [];
		props.scenario.cfg_period_label.map((x, idx) => {
			selOpts[idx] = [];
			if (lookups?.BL_ITEM_OPTS) {
				lookups.BL_ITEM_OPTS.map((y) => {
					if (optionsIds.indexOf(y.id) !== -1) {
						let o = {};
						const thisCfgs = filterObjectsByProperty(
							item.baseline_item_configs,
							"sort_id",
							x.sort_id,
						);
						const thisCfgValues = filterObjectsByProperty(
							item.scenario_item_configs,
							"sort_id",
							x.sort_id,
						);

						o.comp_type_id = y.id;
						o.comp_type_name = y.name;
						o.name = y.name;
						o.update_flag = 0;
						o.sort_id = x.sort_id;
						o.flag1 = y.flag1;

						// Existing Development Program (301):  Accelerate(402), Delay(401), Terminate(403)
						// Existing Procurement Program (302): Add/Cut(404), Cut(405 x), Terminate(403)
						// Existing Forces (303): Add/Cut(404), Cut(405 x)
						// Legacy System (304): Retire more/less(406), RETIRE LESS(407 x)
						// New Development Program (305): New develop(408)

						if (y.flag1 === 1) {
							o.step = item.increment;
							o.defaultValue = 0;
							o.planName = "Planned value";
						}
						o.budgetChange = 10;
						o.budgetChangeName = "Change in Budget";
						o.quantityChange = 5;
						o.budgetChangeName = "Change in Quantity";
						o.changeInBudget = item ? item.item_change_in_budget[idx] : 0;
						o.changeInQuantity = item ? item.item_change_in_quantity[idx] : 0;
						if (item.option_type_id === 301) {
							if (o.comp_type_id === 401) {
								const tempObj = findObjectByProperty(thisCfgValues, "comp_type_id", 401);
								if (tempObj) {
									o.isDelay = tempObj.comp_value1 * 1 > 0;
									o.id = tempObj.id;
									// testing only
									o.budgetChange *= o.isDelay;
									o.quantityChange *= o.isDelay;
								}
							} else if (o.comp_type_id === 402) {
								const tempObj = findObjectByProperty(thisCfgValues, "comp_type_id", 402);
								if (tempObj) {
									o.isAccelerate = tempObj.comp_value1 * 1 > 0;
									o.id = tempObj.id;
									// testing only
									o.budgetChange *= o.isAccelerate;
									o.quantityChange *= o.isAccelerate;
								}
							} else if (o.comp_type_id === 403) {
								const tempObj = findObjectByProperty(thisCfgValues, "comp_type_id", 403);
								if (tempObj) {
									o.isTerminate = tempObj.comp_value1 * 1 > 0;
									o.id = tempObj.id;
									// testing only
									o.budgetChange *= o.isTerminate;
									o.quantityChange *= o.isTerminate;
								}
							}
						} else if (item.option_type_id === 302) {
							if (y.flag1 === 1) {
								// Existing Procurement Program: enables Add, Cut, Terminate
								// shows Baseline Quantity (# of Increments) (#502)
								o.planName = "Planned Procurement";
								const tempObj = findObjectByProperty(thisCfgValues, "comp_type_id", 404);
								if (tempObj) {
									o.defaultValue = tempObj.comp_value1 * 1;
									o.id = tempObj.id;
								}
								o.plan = findObjectByProperty(thisCfgs, "comp_type_id", 502).comp_value * 1;
								o.max = findObjectByProperty(thisCfgs, "comp_type_id", 504).comp_value * 1;
								o.min = findObjectByProperty(thisCfgs, "comp_type_id", 505).comp_value * -1;
							} else {
								const tempObj = findObjectByProperty(thisCfgValues, "comp_type_id", 403);
								if (tempObj) {
									o.isTerminate = tempObj.comp_value1 * 1 > 0;
									o.id = tempObj.id;
									// testing only
									o.budgetChange *= o.isTerminate;
									o.quantityChange *= o.isTerminate;
								}
							}
						} else if (item.option_type_id === 303) {
							if (y.flag1 === 1) {
								// Existing Forces:	enables Add, Cut
								// shows Baseline Quantity (# of Increments) (#502)
								o.planName = "Planned Procurement";
								const tempObj = findObjectByProperty(thisCfgValues, "comp_type_id", 404);
								if (tempObj) {
									o.defaultValue = tempObj.comp_value1 * 1;
									o.id = tempObj.id;
								}

								o.plan = findObjectByProperty(thisCfgs, "comp_type_id", 502).comp_value * 1;
								o.max = findObjectByProperty(thisCfgs, "comp_type_id", 504).comp_value * 1;
								o.min = findObjectByProperty(thisCfgs, "comp_type_id", 505).comp_value * -1;
							}
						} else if (item.option_type_id === 304) {
							if (y.flag1 === 1) {
								// Legacy System:	enables Retire More, Retire Less
								// shows Planned Retirements (#506)
								// Planned Retirements + The sum of (Retire More) of each years <= Total Inventory (total_existing_inventory)
								o.planName = "Planned Retirement";
								const tempObj = findObjectByProperty(thisCfgValues, "comp_type_id", 406);
								if (tempObj) {
									o.defaultValue = tempObj.comp_value1 * 1;
									o.id = tempObj.id;
								}
								o.plan = findObjectByProperty(thisCfgs, "comp_type_id", 506).comp_value;
								o.max =
									parseInt(item.total_existing_inventory) -
									(parseInt(item.total_planned) + parseInt(item.total_change)) +
									parseInt(o.defaultValue);
								o.min = o.plan * -1;
							}
						} else if (item.option_type_id === 305) {
							const tempObj = findObjectByProperty(thisCfgValues, "comp_type_id", 408);
							if (tempObj) {
								o.isNewDevelop = tempObj.comp_value1 * 1 > 0;
								o.id = tempObj.id;
								// testing only
								o.budgetChange *= o.isNewDevelop;
								o.quantityChange *= o.isNewDevelop;
							}
						}
						if (y.flag1 === 1) {
							o.budgetChange = o.defaultValue * 10;
							o.quantityChange = o.defaultValue * 5;
						}

						selOpts[idx].push(o);
					}
					return false;
				});
			}
			return false;
		});

		return selOpts;
	};

	// get max value allowed - Letacy System - Retire more/less - 8/4/2025
	const totalPeriodSum = currValues.reduce((a, b) => parseInt(a) + parseInt(b), 0);
	const globalMax = parseInt(thisItem.total_existing_inventory) - parseInt(thisItem.total_planned) || 0;
	const atMax = thisItem.option_type_id === 304 ? totalPeriodSum >= globalMax : false;

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	// debugLog(2, `\n======== [ScenarioItem - SINGLE] SINGLE (render# ${renderCount.current}) ========\n`, {
	// 	itemName: thisItem.name,
	// 	props: props,
	// });
	if (thisItem.id === 953) {
		debugLog(9, "[ScenarioItem][FLOW] renderState", {
			thisItem: thisItem,
			currValues: currValues,
			selectionOptions: selectionOptions,
			totalPeriodSum: totalPeriodSum,
			globalMax: globalMax,
			atMax: atMax,
		});
		// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");
	}

	const hasDescription = thisItem.description?.trim?.();
	const itemLabel = (
		<label className={styles.configLabel} id={thisItem.id} onClick={showItemHandler}>
			<span className="info">
				{isDeveloper() && (
					<>
						{htmlParser.parse(highlight(props.keywords, thisItem.name, false))} (item id:{" "}
						{thisItem.id})
					</>
				)}
				{(thisItem.option_type_id === 304 ||
					thisItem.option_type_id === 302 ||
					thisItem.option_type_id === 303) && (
					<span> (Total inventory: {thisItem.total_existing_inventory})</span>
				)}
			</span>
		</label>
	);
	const itemLabelWithTooltip = hasDescription ? (
		<MetreaTooltip content={thisItem.description}>
			{itemLabel}
		</MetreaTooltip>
	) : (
		itemLabel
	);
	const itemImage = (
		<span className="info" id={thisItem.id} onClick={showItemHandler}>
			<img
				alt="not found"
				width={
					thisItem.thumbnail && thisItem.thumbnail.data && thisItem.thumbnail.data.length !== 0
						? "180px"
						: "100px"
				}
				src={
					thisItem.thumbnail && thisItem.thumbnail.data && thisItem.thumbnail.data.length !== 0
						? `data:image/*;base64,${Buffer.from(thisItem.thumbnail.data).toString("base64")}`
						: imgPlaceholder
				}
			/>
		</span>
	);
	const itemImageWithTooltip = hasDescription ? (
		<MetreaTooltip content={thisItem.description}>
			{itemImage}
		</MetreaTooltip>
	) : (
		itemImage
	);

	return (
		<Box bg="white" color={mColor.MidnightBlue} data-testid={`ScenarioItem-${thisItem.id}`} p="10px">
			<Box display="flex" alignItems="center">
				{isExpanded && (
					<MetreaTooltip content={`Collapse this item`}>
						<Icon as={SubtractSquareRegular} boxSize="24px" onClick={expandedSwitchHandler} />
					</MetreaTooltip>
				)}
				{!isExpanded && (
					<MetreaTooltip content={`Expand this item`}>
						<Icon as={AddSquareRegular} boxSize="24px" onClick={expandedSwitchHandler} />
					</MetreaTooltip>
				)}
				{itemLabelWithTooltip}
			</Box>

			{isExpanded && (
				<Box display="flex" overflow="auto">
					{/* Item photo */}
					<Box
						pt="20px"
						px="10px"
						pb="0px"
						mb={3}
						minWidth="200px"
						display="flex"
						flexDirection="column"
					>
						<Box
							textAlign="center"
							fontWeight={600}
							flex="1 1 auto"
							display="flex"
							alignItems="center"
							justifyContent="center"
							w="180px"
							height="auto"
						>
							{thisItem && itemImageWithTooltip}
						</Box>
					</Box>

					{/* Item options */}
					{props.scenario.cfg_period_label.map((x, idx) => {
						return (
							<Box key={idx}>
								<ItemOptions
									// key={idx}
									key={`${thisItem.id}-${idx}`}
									index={idx}
									item={thisItem}
									mode={props.mode}
									options={selectionOptions[idx]}
									iconStatus={iconStatus}
									currValues={currValues}
									individualLabel={idx === 0 ? true : false}
									showPlanValue={true}
									firstItem={idx === 0 ? true : false}
									topLabel={x.comp_value}
									atMax={atMax}
									onChange={optionChangeHandler}
									onError={props.onError}
								/>
							</Box>
						);
					})}

					{/* Change Summary */}
					<Box
						p="0"
						w="100%"
						minW="100px"
						display="flex"
						flexDir="column"
						flex="1 1 100%"
						pb="15px"
					>
						<Box flex="1 1 auto">
							<Box textAlign="center" fontWeight={600} flex="1 1 auto">
								<label className={itemStyles.topLabel}>
									Net Change
									<br />
									{`(${props.scenario.cfg_period_label[0].comp_value}-
									${props.scenario.cfg_period_label[props.scenario.cfg_period_label.length - 1].comp_value})`}
								</label>
							</Box>
							{(thisItem.option_type_id === 302 ||
								thisItem.option_type_id === 303 ||
								thisItem.option_type_id === 304) && (
								<>
									<Box textAlign="center">
										<label className={itemStyles.label}>
											{typeof thisItem.total_planned !== "undefined" &&
												applyFormatToNumber(
													parseInt(thisItem.total_planned),
													0,
													null,
													null,
													true,
												)}{" "}
										</label>
									</Box>
									<Box textAlign="center">
										<label className={itemStyles.label}>
											{typeof thisItem.total_change !== "undefined" &&
												applyFormatToNumber(
													parseInt(thisItem.total_change),
													0,
													null,
													null,
													true,
												)}{" "}
										</label>
									</Box>
								</>
							)}
						</Box>

						{/* Empty spacing */}
						<Box flex="1" w="100%">
							&nbsp;
						</Box>

						<Box flex="0 0 20px" w="100%" textAlign="center">
							<label className={itemStyles.label}>
								{applyFormatToNumber(
									thisItem.item_change_in_budget.reduce((a, b) => a + b, 0),
									props.item.currency_display_decimal,
									props.item.currency_symbol,
									props.item.currency_unit,
									true,
								)}
							</label>
						</Box>
						{showTesting() && (
							<Box flex="0 0 20px" w="100%" textAlign="center">
								<label className={itemStyles.label} style={{ color: "lightcoral" }}>
									{thisItem.item_change_in_budget.reduce((a, b) => a + b, 0)}
								</label>
							</Box>
						)}
						<Box flex="0 0 20px" w="100%" textAlign="center">
							-
						</Box>
						{showTesting() && (
							<Box flex="0 0 20px" w="100%" textAlign="center">
								<label className={itemStyles.label} style={{ color: "lightcoral" }}>
									{applyFormatToNumber(
										thisItem.item_change_in_quantity.reduce((a, b) => a + b, 0),
										0,
										null,
										null,
										true,
									)}{" "}
								</label>
							</Box>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};

/**
 * ScenarioItems
 *
 * Renders and manages expandable/collapsible groups of items for scenario selection.
 * Groups can be by Military Service, Domain, or Category. Handles expansion state per group,
 * propagates changes from child ScenarioItem components upward.
 *
 * Props:
 * @param {object} props
 * @param {object} props.item - The current scenario data (baseline, items, configs, etc.)
 * @param {string} props.mode - "view" or "edit" mode for child items.
 * @param {number} props.displayItemsBy - Grouping method: 111 (Service), 112 (Domain), 113 (Category)
 * @param {array} props.groups - Array of group objects for display.
 * @param {number} props.groupBy - The currently selected group id.
 * @param {boolean[]} props.isExpanded - Array tracking expansion state for groups.
 * @param {array} props.visibleItems - The currently visible scenario items (post-filter).
 * @param {object} props.filter - Search/filter config for keywords, etc.
 * @param {function} props.onChange - Callback for changes from ScenarioItem.
 * @param {function} props.onShowItem - Handler for showing item detail modal.
 * @param {function} props.onError - Handler for error events.
 *
 * @component
 */
const ScenarioItems = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	useEffect(() => {
		debugLog(2, "[ScenarioItems][FLOW] mounted", {});
	}, []);

	const pageMode = props.mode;

	const [currScenario, setCurrScenario] = useState(props.item);
	const [isExpanded, setIsExpanded] = useState([]);
	/**
	 * display items by types
	 * 111: by Service (military_service_id)
	 * 112: by Domain (domain_id)
	 * 113: by category (category_id)
	 */
	const [groupId] = useState(113);
	const [subGroups, setSubGroups] = useState([]);
	const [groupIdx, setGroupIdx] = useState(0);
	const arrayGroupDetails = [
		{
			comp_type_id: 111,
			name: "Military Service",
			allGroupName: "Military Services",
			column: "military_service_id",
			code: "military_service",
		},
		{
			comp_type_id: 112,
			name: "Domain",
			allGroupName: "Domains",
			column: "domain_id",
			code: "domain",
		},
		{
			comp_type_id: 113,
			name: "Category",
			allGroupName: "Categories",
			column: "category_id",
			code: "category",
		},
		{
			comp_type_id: -1,
			name: "Search",
			allGroupName: "Search",
			column: "-",
			code: "-",
		},
	];

	// --- Effect: Syncs currScenario with latest props.item on change
	useEffect(() => {
		setCurrScenario(props.item);
		debugLog(3, "[ScenarioItems][STATE] props.item changed", { propsItem: props.item });
	}, [props.item]);

	// --- Effect: Updates subGroups (active groupings) whenever grouping or baseline_configs changes.Filters out groups with zero items
	useEffect(() => {
		const allSubGroups = filterObjectsByProperty(
			currScenario.baseline_configs,
			"comp_type_id",
			parseInt(groupId),
		);

		let column = "";
		switch (parseInt(groupId)) {
			case 111:
				column = "military_service_id";
				break;
			case 112:
				column = "domain_id";
				break;
			case 113:
				column = "category_id";
				break;
			default:
				column = "name";
				break;
		}

		allSubGroups.map((x) => {
			x.name = x.comp_value;
			let tempArray = props.visibleItems.filter((y) => {
				return y[column] === x.id;
			});
			x.itemCount = tempArray.length;
			return false;
		});
		setSubGroups([...allSubGroups.filter((x) => x.itemCount > 0)]);

		debugLog(4, "[ScenarioItems][PROCESS] group setup", {
			groupId: groupId,
			allSubGroups: allSubGroups,
			propsVisibleItems: props.visibleItems,
			currScenarioBaselineConfigs: currScenario.baseline_configs,
		});
	}, [groupId, currScenario.baseline_configs, props.visibleItems]);

	// --- Effect: Resets expansion state for groups/items when display grouping or group array changes
	useEffect(() => {
		const arrayLength = props.groupBy > 0 ? props.groups.length : 1;

		// [SAFEGUARD] baseline_items may be undefined during Scenario.real.test
		const itemCount = Array.isArray(currScenario?.baseline_items)
			? currScenario.baseline_items.length
			: 0;
		setIsExpanded(
			Array(arrayLength)
				.fill(null)
				.map(() => Array(itemCount).fill(false)),
		);

		debugLog(4, "[ScenarioItems][PROCESS] reset isExpanded", {
			groupBy: props.groupBy,
			isExpanded: isExpanded,
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.displayItemsBy, props.groups]);

	// --- Effect: Updates groupIdx whenever groups or groupBy changes
	useEffect(() => {
		if (props.displayItemsBy === 111 || props.displayItemsBy === 112) {
			setGroupIdx(props.groups.findIndex((x) => x.id === props.groupBy));
		} else setGroupIdx(0);

		debugLog(3, "[ScenarioItems][STATE] groupIdx changed", {
			groupBy: props.groupBy,
			groupIdx: groupIdx,
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.groups, props.groupBy]);

	// Effect: Syncs expansion state with external isExpanded prop, per group.
	// Avoids array mutation via immutable update.
	useUpdateEffect(() => {
		setIsExpanded([
			...isExpanded.map((x, idx) => (idx === groupIdx ? x.fill(props.isExpanded[groupIdx]) : x)),
		]);

		debugLog(3, "[ScenarioItems][STATE] isExpanded synced", {
			isExpanded: isExpanded,
			propsIsExpanded: props.isExpanded,
			groupIdx: groupIdx,
		});
	}, [props.isExpanded]);

	/**
	 * Handles toggling expansion/collapse for a group.
	 * Uses immutable state update to avoid array mutation bugs.
	 *
	 * @param {React.MouseEvent} event - The click event from the expand/collapse icon.
	 * @param {number} index - Index of the group to expand/collapse.
	 */
	const expandedSwitchHandler = (event, index) => {
		let tempArray = isExpanded;
		tempArray[groupIdx][index] = !tempArray[groupIdx][index];
		setIsExpanded([...tempArray]);

		debugLog(3, "[ScenarioItems][ACTION] expandedSwitchHandler", {
			groupIdx: groupIdx,
			index: index,
			isExpanded: tempArray[groupIdx][index],
		});
	};

	/**
	 * Propagates option/selection changes from ScenarioItem upward to parent.
	 * @param {object} option - The updated option object (from child).
	 */
	const optionChangeHandler = (option) => {
		debugLog(3, "[ScenarioItems][ACTION] optionChangeHandler", { option: option });

		props.onChange(option);
	};

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[ScenarioItems][FLOW] render", {
		renderCount: renderCount.current,
		currScenario: currScenario,
	});
	debugLog(9, "[ScenarioItems][FLOW] renderState", {
		props: props,
		currScenario: currScenario,
		groupId: groupId,
		subGroups: subGroups,
		groupIdx: groupIdx,
		isExpanded: isExpanded,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<div data-testid="ScenarioItems">
			<Stack gap={1} direction="column">
				{subGroups &&
					subGroups.length > 0 &&
					subGroups.map((group, idx1) => {
						const bgColor = group.color && group.color !== "null" ? group.color : "#eee";
						const fontColor =
							group.color && group.color !== "null"
								? getContrastYIQ(group.color)
									? mColor.MidnightBlue
									: mColor.White
								: mColor.MidnightBlue;
						const groupDetails = findObjectByProperty(arrayGroupDetails, "comp_type_id", groupId);
						group = { ...group, ...groupDetails };

						return (
							<Box key={idx1} bg={bgColor} color={fontColor} mt="2px">
								<Box p="8px">
									<Box display="flex" alignItems="center">
										{isExpanded[groupIdx][idx1] && (
											<MetreaTooltip
												content={`Collapse ${group.comp_value} ${group.name}`}
											>
												<Icon
													as={SubtractSquareRegular}
													boxSize="24px"
													onClick={(event) => expandedSwitchHandler(event, idx1)}
												/>
											</MetreaTooltip>
										)}
										{!isExpanded[groupIdx][idx1] && (
											<MetreaTooltip
												content={`Expand ${group.comp_value} ${group.name}`}
											>
												<Icon
													as={AddSquareRegular}
													boxSize="24px"
													onClick={(event) => expandedSwitchHandler(event, idx1)}
												/>
											</MetreaTooltip>
										)}
										<label
											className={styles.configLabel}
											style={{ fontSize: "0.9rem", color: fontColor }}
										>
											{group.comp_value}
											&nbsp;({group.itemCount} item
											{(group?.itemCount ?? 0) > 1 ? "s" : ""})
										</label>
									</Box>

									{isExpanded[groupIdx][idx1] && (
										<Box p="10px" overflow="auto">
											<Stack gap={2} direction="column">
												{props.visibleItems.map((x, idx2) => {
													if (x[group.column] === group.id)
														return (
															<Card.Root key={`${group.id}-${x.id}`}>
																<Card.Body p="1rem">
																	<ScenarioItem
																		key={`${currScenario.id}-${x.id}`}
																		item={x}
																		revertKey={props.revertKey}
																		group={group}
																		mode={pageMode}
																		expanded={isExpanded[groupIdx][idx1]}
																		scenario={currScenario}
																		keywords={props.filter.keywords}
																		onChange={optionChangeHandler}
																		onShowItem={props.onShowItem}
																		onError={props.onError}
																	/>
																</Card.Body>
															</Card.Root>
														);
													return false;
												})}
											</Stack>
										</Box>
									)}
								</Box>
							</Box>
						);
					})}
			</Stack>
		</div>
	);
};

export default ScenarioItems;
